import React, { useEffect, useRef, useState } from 'react';
import { Input, InputRef, Select } from 'antd';
import Arrow from '@svg/chevron.svg?react';
import SearchIcon from '@svg/search.svg?react';
import RemoveIcon from '@svg/x-icon.svg?react';
import './custom-select.scss';
import { observer } from 'mobx-react-lite';
import { SelectProps } from 'antd/lib/select';
import Loader from '../loader/loader';
import { selectFitPositionWithScroll } from '../../../utils/constants';
import { keyboardClickable } from '../../../utils/a11y-utils';

interface CustomSelectProps {
    options: { key:number;  value: number; label: string; data?: any }[];
    onSelect?: (value: any) => void;
    onChange?: (values: any[]) => void;
    value?: any;
    placeholder: string;
    disabled?: boolean;
    emptyMessage: string;
    LabelRenderer?: React.FC<{ data: any }>;
    className?: string;
    selectProps?: SelectProps;
    isOptionDisabled?: (data: any) => boolean;
}

const CustomSelect: React.FC<CustomSelectProps> = observer(({
                                                                options,
                                                                onSelect,
                                                                onChange,
                                                                value,
                                                                placeholder,
                                                                disabled = false,
                                                                emptyMessage,
                                                                LabelRenderer,
                                                                className,
                                                                selectProps,
                                                                isOptionDisabled
                                                            }) => {
    const [filteredOptions, setFilteredOptions] = useState(options);
    const [searchTerm, setSearchTerm] = useState('');
    const inputRef = useRef<InputRef>(null);

    useEffect(() => {
        if (searchTerm === '') {
            setFilteredOptions(options);
        } else {
            const lowercasedFilter = searchTerm.toLowerCase();
            const filteredData = options.filter((item) =>
                item.label.toLowerCase().includes(lowercasedFilter)
            );
            setFilteredOptions(filteredData);
        }
    }, [searchTerm, options]);

    const onSearch = (e: any) => {
        setSearchTerm(e.target.value);
    };

    const selectItem = (value: any) => {
        onSelect?.(value);
        setSearchTerm('');
    };

    const dropdownRender = (menu: React.ReactElement) => (
        <>
            <div className="search-wrapper">
                <Input ref={inputRef} autoFocus value={searchTerm} onChange={onSearch} onKeyDown={(e)=> e.stopPropagation()} prefix={<SearchIcon/>}
                       suffix={<span aria-label="Clear search" {...keyboardClickable(() => setSearchTerm(''))}><RemoveIcon /></span>} />
            </div>
            <div className={`list-wrapper  ${className}`}>{menu}</div>
        </>
    );

    return (
        <Select
            className={`custom-select ${className}`}
            popupClassName={`custom-select-dropdown ${className}`}
            disabled={disabled}
            placeholder={placeholder}
            onSelect={selectItem}
            onChange={onChange}
            value={value}
            filterOption={false}
            dropdownRender={dropdownRender}
            getPopupContainer={selectFitPositionWithScroll}
            suffixIcon={<Arrow/>}
            notFoundContent={selectProps?.loading ? <Loader type={'component'} /> :
                <span className="empty-message">{emptyMessage}</span>}
            onInputKeyDown={() => inputRef.current?.focus()}
            {...selectProps}
        >
            {filteredOptions.map((option) => (
                    <Select.Option key={option.key} value={option.value} disabled={isOptionDisabled?.(option.data)}>
                        {LabelRenderer ? <LabelRenderer data={option.data}/> : option.label}
                    </Select.Option>
                ))
            }
        </Select>
    );
});

export default CustomSelect;